import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, TextField, Typography } from "@mui/material";
import { setEdgeOtaChannel } from "../../api/otaTenantApi";
import { errDetail } from "./otaHelpers";

// Sequential per-edge PATCH with progress + per-edge result list.
export default function ChannelDialog({ open, edges, onClose }) {
  const { t } = useTranslation();
  const token = useSelector((s) => s.session?.accessToken);
  const isSuper = useSelector((s) => s.session?.me?.global_role === "superadmin");
  const [channel, setChannel] = useState("stable");
  const [rows, setRows] = useState(null); // null = not started
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setRows(null); setChannel("stable"); }
  }, [open]);

  const run = async () => {
    setBusy(true);
    setRows([]);
    for (const e of edges) {
      let r;
      try {
        await setEdgeOtaChannel(token, e.id, channel);
        r = { id: e.id, name: e.name, ok: true };
      } catch (err) {
        r = { id: e.id, name: e.name, ok: false, msg: errDetail(err, t("edges.ota.channel.failed")) };
      }
      setRows((p) => [...p, r]);
    }
    setBusy(false);
  };
  const done = rows && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : () => onClose(!!rows)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } }}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{t("edges.ota.channel.title")}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {!rows ? (
          <TextField select fullWidth size="small" label={t("edges.ota.channel.label", { count: edges.length })} value={channel} onChange={(e) => setChannel(e.target.value)} sx={{ mt: 1, "& .MuiInputBase-root": { color: "var(--text-primary)" }, "& .MuiInputLabel-root": { color: "var(--text-muted)" } }}>
            <MenuItem value="stable">stable</MenuItem>
            <MenuItem value="canary">canary</MenuItem>
            {isSuper && <MenuItem value="dev">dev</MenuItem>}
          </TextField>
        ) : (
          <>
            <Typography sx={{ color: "var(--text-muted)", mb: 1 }}>{t("edges.ota.channel.progress", { done: rows.length, total: edges.length })}</Typography>
            {rows.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border-subtle)", fontSize: 14 }}>
                <b style={{ flex: 1 }}>{r.name || r.id}</b>
                <span style={{ color: r.ok ? "var(--success)" : "var(--danger)" }}>{r.ok ? t("edges.ota.channel.updated") : r.msg}</span>
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        {!rows && <Button onClick={() => onClose(false)} sx={{ color: "var(--text-muted)" }}>{t("common.cancel")}</Button>}
        {!rows && <Button onClick={run} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("edges.ota.channel.apply")}</Button>}
        {rows && <Button onClick={() => onClose(true)} disabled={!done} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("common.close")}</Button>}
      </DialogActions>
    </Dialog>
  );
}
